// Which car specs have a real value worth showing (1NOW-11141).
//
// A spec the host never filled in arrives as null, "", "N/A", "-" or, for a
// count, 0. Cards and the car page drop those rather than print a dash: to an
// ad visitor a dash looks unfinished. Hosts still see what is missing in FMS.
import { cardSpecs } from "@1-now/booking-core";

const PLACEHOLDERS = new Set(["n/a", "na", "n.a.", "-", "--", "—", "–", "null", "undefined", "unknown"]);

/** The trimmed text of a spec, or null when it is empty or a placeholder. */
export const specText = (v: unknown): string | null => {
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : null;
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t && !PLACEHOLDERS.has(t.toLowerCase()) ? t : null;
};

/**
 * A count (seats, doors) or a year: a positive number, else null. For these 0
 * means "not set", and text that is not a number ("N/A", "five") is not a count.
 */
export const specNumber = (v: unknown): number | null => {
  const t = specText(v);
  const n = t === null ? NaN : Number(t);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export type CarSpecValues = {
  seats: number | null;
  doors: number | null;
  /** "Automatic", "Semi Automatic": labelled the way the package labels it. */
  transmission: string | null;
  color: string | null;
};

/**
 * 1NOW-11175: a chip on the card is a clean label or no chip. A count past what a car has (the
 * "11 doors" a host typed) and a transmission that is not one ("Transmission", a bare letter
 * that means nothing) are junk, like the placeholders above, and are left out. "A" and "M"
 * are the usual shorthand and read as Automatic and Manual.
 */
const MAX_SEATS = 20;
const MAX_DOORS = 6;
const MAX_COLOR_LENGTH = 20;
const TRANSMISSION_WORDS = /^(auto|manual|semi|cvt|e cvt|dct|dual|tiptronic|sequential|stick)/;

const transmissionLabel = (raw: unknown): string | null => {
  const text = specText(raw);
  if (!text) return null;
  const key = text.toLowerCase().replace(/[\s_-]+/g, " ");
  if (key === "a" || key === "at") return "Automatic";
  if (key === "m" || key === "mt") return "Manual";
  if (!TRANSMISSION_WORDS.test(key)) return null;
  if (/^(e )?(cvt|dct)$/.test(key)) return text.toUpperCase();
  return cardSpecs({ transmission_type: text }).find((s) => s.key === "transmission")?.label || null;
};

const capped = (n: number | null, max: number) => (n !== null && n <= max ? n : null);

export const carSpecValues = (car: any): CarSpecValues => {
  const color = specText(car?.color);
  return {
    seats: capped(specNumber(car?.seats), MAX_SEATS),
    doors: capped(specNumber(car?.doors), MAX_DOORS),
    transmission: transmissionLabel(car?.transmission_type),
    color: color && color.length <= MAX_COLOR_LENGTH ? color : null,
  };
};

export type SpecChip = { key: "seats" | "doors" | "transmission" | "color"; label: string; sub: string };

/**
 * The chips Design B's card shows, left to right: seats, doors, then the
 * transmission, or the colour when the car has no transmission. Only the ones
 * with a value; an empty list means the card has no spec strip at all.
 */
export const specChips = (car: any): SpecChip[] => {
  const { seats, doors, transmission, color } = carSpecValues(car);
  const chips: SpecChip[] = [];
  if (seats !== null) chips.push({ key: "seats", label: String(seats), sub: "Seats" });
  if (doors !== null) chips.push({ key: "doors", label: String(doors), sub: "Doors" });
  if (transmission) chips.push({ key: "transmission", label: transmission, sub: "Trans." });
  else if (color) chips.push({ key: "color", label: color, sub: "Color" });
  return chips;
};
